import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../context/StoreContext';
import { shiftsAPI, locationsAPI, extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';

const TABS = ['my-shift', 'history', 'daily-closure'];

const Shifts = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();
  const storeId = selectedStore?.id;
  const [activeTab, setActiveTab] = useState('my-shift');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // My Shift
  const [currentShift, setCurrentShift] = useState(null);
  const [openForm, setOpenForm] = useState({ opening_cash: '', location_id: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ actual_cash: '', notes: '' });
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [cashMovements, setCashMovements] = useState([]);
  const [movementForm, setMovementForm] = useState({ type: 'deposit', amount: '', reason: '' });
  const [locations, setLocations] = useState([]);
  const [elapsed, setElapsed] = useState('');
  const timerRef = useRef(null);

  // History
  const [shifts, setShifts] = useState([]);
  const [historyFilter, setHistoryFilter] = useState({ status: '', date_from: '', date_to: '', staff: '' });
  const [expandedShift, setExpandedShift] = useState(null);
  const [expandedMovements, setExpandedMovements] = useState([]);

  // Daily Closure
  const [closureDate, setClosureDate] = useState(new Date().toISOString().split('T')[0]);
  const [closureData, setClosureData] = useState(null);

  const loadCurrentShift = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await shiftsAPI.getCurrent(storeId);
      setCurrentShift(res.data);
      if (res.data?.id) {
        const mvRes = await shiftsAPI.listCashMovements(res.data.id);
        setCashMovements(Array.isArray(mvRes.data) ? mvRes.data : mvRes.data.movements || []);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setCurrentShift(null);
        setCashMovements([]);
      }
    }
  }, [storeId]);

  const loadLocations = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await locationsAPI.list(storeId);
      setLocations(res.data.locations || []);
    } catch { /* ignore */ }
  }, [storeId]);

  useEffect(() => {
    loadCurrentShift();
    loadLocations();
  }, [loadCurrentShift, loadLocations]);

  // Elapsed timer
  useEffect(() => {
    if (currentShift?.opened_at) {
      const update = () => {
        const start = new Date(currentShift.opened_at);
        const diff = Math.floor((Date.now() - start.getTime()) / 1000);
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        setElapsed(`${h}h ${m}m`);
      };
      update();
      timerRef.current = setInterval(update, 60000);
      return () => clearInterval(timerRef.current);
    }
  }, [currentShift]);

  const handleOpenShift = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await shiftsAPI.open({ store_id: storeId, opening_cash: Number(openForm.opening_cash), location_id: openForm.location_id || undefined, notes: openForm.notes || undefined });
      showToast('Shift opened', 'success');
      loadCurrentShift();
      setOpenForm({ opening_cash: '', location_id: '', notes: '' });
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to open shift'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseShift = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await shiftsAPI.close(currentShift.id, { actual_cash: Number(closeForm.actual_cash), notes: closeForm.notes || undefined });
      showToast('Shift closed', 'success');
      setShowCloseForm(false);
      setCloseForm({ actual_cash: '', notes: '' });
      loadCurrentShift();
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to close shift'));
    } finally {
      setLoading(false);
    }
  };

  const handleStartBreak = async () => {
    try {
      await shiftsAPI.startBreak(currentShift.id);
      showToast('Break started', 'success');
      loadCurrentShift();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleEndBreak = async () => {
    try {
      await shiftsAPI.endBreak(currentShift.id);
      showToast('Break ended', 'success');
      loadCurrentShift();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleAddMovement = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await shiftsAPI.addCashMovement(currentShift.id, {
        type: movementForm.type,
        amount: Number(movementForm.amount),
        reason: movementForm.reason,
      });
      setMovementForm({ type: 'deposit', amount: '', reason: '' });
      loadCurrentShift();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadShiftHistory = useCallback(async () => {
    if (!storeId) return;
    try {
      const params = {};
      if (historyFilter.status) params.status = historyFilter.status;
      if (historyFilter.date_from) params.date_from = historyFilter.date_from;
      if (historyFilter.date_to) params.date_to = historyFilter.date_to;
      const res = await shiftsAPI.list(storeId, params);
      setShifts(Array.isArray(res.data) ? res.data : res.data.shifts || []);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [storeId, historyFilter]);

  const handleExpandShift = async (shiftId) => {
    if (expandedShift === shiftId) {
      setExpandedShift(null);
      return;
    }
    try {
      const res = await shiftsAPI.listCashMovements(shiftId);
      setExpandedMovements(Array.isArray(res.data) ? res.data : res.data.movements || []);
      setExpandedShift(shiftId);
    } catch { setExpandedShift(shiftId); setExpandedMovements([]); }
  };

  const loadDailyClosure = async () => {
    try {
      setLoading(true);
      const res = await shiftsAPI.dailyClosure(storeId, closureDate);
      setClosureData(res.data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') loadShiftHistory();
  }, [activeTab, loadShiftHistory]);

  const formatVariance = (val) => {
    if (val == null) return '-';
    const n = Number(val);
    if (n > 0) return <span className="variance-positive">+${n.toFixed(2)}</span>;
    if (n < 0) return <span className="variance-negative">-${Math.abs(n).toFixed(2)}</span>;
    return <span className="variance-zero">$0.00</span>;
  };

  const expectedCash = currentShift
    ? Number(currentShift.opening_cash || 0) + cashMovements.reduce((sum, m) => {
        if (m.type === 'deposit') return sum + Number(m.amount || 0);
        if (m.type === 'withdrawal' || m.type === 'payout') return sum - Number(m.amount || 0);
        return sum;
      }, 0) + Number(currentShift.total_sales || 0)
    : 0;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Shifts & Cash Drawer</h1>
      </div>

      {error && <div className="alert alert-danger">{error}<button className="inline-alert-close" onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>x</button></div>}

      <div className="tab-bar">
        {TABS.map(tab => (
          <button key={tab} className={`tab-btn ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
            {tab === 'my-shift' ? 'My Shift' : tab === 'history' ? 'History' : 'Daily Closure'}
          </button>
        ))}
      </div>

      {/* Tab 1: My Shift */}
      {activeTab === 'my-shift' && (
        <>
          {!currentShift ? (
            <div className="card">
              <div className="card-header"><h3 className="card-title">Open Shift</h3></div>
              <div className="card-body">
                <form onSubmit={handleOpenShift}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Opening Cash *</label>
                      <input type="number" className="form-input" step="0.01" value={openForm.opening_cash}
                        onChange={(e) => setOpenForm({ ...openForm, opening_cash: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Location</label>
                      <select className="form-input" value={openForm.location_id} onChange={(e) => setOpenForm({ ...openForm, location_id: e.target.value })}>
                        <option value="">Select location</option>
                        {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <input className="form-input" value={openForm.notes} onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })} />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>Open Shift</button>
                </form>
              </div>
            </div>
          ) : (
            <>
              {/* KPI Row */}
              <div className="kpi-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="kpi-card">
                  <div className="kpi-label">Opening Cash</div>
                  <div className="kpi-value">${Number(currentShift.opening_cash || 0).toFixed(2)}</div>
                </div>
                <div className="kpi-card primary">
                  <div className="kpi-label">Expected Cash</div>
                  <div className="kpi-value">${expectedCash.toFixed(2)}</div>
                </div>
                <div className="kpi-card success">
                  <div className="kpi-label">Total Sales</div>
                  <div className="kpi-value">${Number(currentShift.total_sales || 0).toFixed(2)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Duration</div>
                  <div className="kpi-value">{elapsed}</div>
                </div>
              </div>

              {/* Break Controls */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="card-body">
                  <div className="shifts-break-controls" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.85rem' }}>Break:</strong>
                    {currentShift.on_break ? (
                      <button className="btn btn-sm btn-warning" onClick={handleEndBreak}>End Break</button>
                    ) : (
                      <button className="btn btn-sm btn-secondary" onClick={handleStartBreak}>Start Break</button>
                    )}
                    {currentShift.on_break && <span className="badge badge-warning">On Break</span>}
                  </div>
                </div>
              </div>

              {/* Cash Movement */}
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div className="card-header"><h3 className="card-title">Cash Movements</h3></div>
                <div className="card-body">
                  <form onSubmit={handleAddMovement} style={{ marginBottom: '1rem' }}>
                    <div className="form-grid">
                      <div className="form-group">
                        <label className="form-label">Type</label>
                        <select className="form-input" value={movementForm.type} onChange={(e) => setMovementForm({ ...movementForm, type: e.target.value })}>
                          <option value="deposit">Deposit</option>
                          <option value="withdrawal">Withdrawal</option>
                          <option value="payout">Payout</option>
                          <option value="adjustment">Adjustment</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Amount</label>
                        <input type="number" className="form-input" step="0.01" value={movementForm.amount}
                          onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })} required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Reason</label>
                        <input className="form-input" value={movementForm.reason}
                          onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })} />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-sm btn-primary" disabled={loading}>Add Movement</button>
                  </form>

                  {cashMovements.length > 0 && (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr><th>Type</th><th>Amount</th><th>Reason</th><th>Time</th></tr>
                        </thead>
                        <tbody>
                          {cashMovements.map((m, i) => (
                            <tr key={i}>
                              <td>{m.type}</td>
                              <td>${Number(m.amount || 0).toFixed(2)}</td>
                              <td>{m.reason || '-'}</td>
                              <td>{m.created_at ? new Date(m.created_at).toLocaleTimeString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Close Shift */}
              {!showCloseForm ? (
                <button className="btn btn-danger" onClick={() => setShowCloseForm(true)}>Close Shift</button>
              ) : (
                <div className="card">
                  <div className="card-header"><h3 className="card-title">Close Shift</h3></div>
                  <div className="card-body">
                    <div style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
                      <strong>Expected Cash:</strong> ${expectedCash.toFixed(2)}
                    </div>
                    <form onSubmit={handleCloseShift}>
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Actual Cash *</label>
                          <input type="number" className="form-input" step="0.01" value={closeForm.actual_cash}
                            onChange={(e) => setCloseForm({ ...closeForm, actual_cash: e.target.value })} required />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Notes</label>
                          <input className="form-input" value={closeForm.notes}
                            onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} />
                        </div>
                      </div>
                      {closeForm.actual_cash && (
                        <div style={{ marginBottom: '1rem', fontSize: '0.9rem' }}>
                          Variance: {formatVariance(Number(closeForm.actual_cash) - expectedCash)}
                        </div>
                      )}
                      <div className="form-actions">
                        <button type="submit" className="btn btn-danger" disabled={loading}>Confirm Close</button>
                        <button type="button" className="btn btn-secondary" onClick={() => setShowCloseForm(false)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Tab 2: History */}
      {activeTab === 'history' && (
        <>
          <div className="filter-bar shifts-history-filters" style={{ marginBottom: '1rem' }}>
            <select className="form-input shifts-history-filter-input" value={historyFilter.status} onChange={(e) => setHistoryFilter({ ...historyFilter, status: e.target.value })} style={{ maxWidth: '160px' }}>
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
            <input type="date" className="form-input shifts-history-filter-input" value={historyFilter.date_from} onChange={(e) => setHistoryFilter({ ...historyFilter, date_from: e.target.value })} style={{ maxWidth: '180px' }} />
            <input type="date" className="form-input shifts-history-filter-input" value={historyFilter.date_to} onChange={(e) => setHistoryFilter({ ...historyFilter, date_to: e.target.value })} style={{ maxWidth: '180px' }} />
          </div>

          {/* Desktop Table */}
          <div className="card hidden-mobile">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Started</th>
                    <th>Ended</th>
                    <th>Duration</th>
                    <th>Opening</th>
                    <th>Expected</th>
                    <th>Actual</th>
                    <th>Variance</th>
                    <th>Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map(s => (
                    <React.Fragment key={s.id}>
                      <tr onClick={() => handleExpandShift(s.id)} style={{ cursor: 'pointer' }}>
                        <td>{s.staff_name || s.user_name || '-'}</td>
                        <td>{s.opened_at ? new Date(s.opened_at).toLocaleString() : '-'}</td>
                        <td>{s.closed_at ? new Date(s.closed_at).toLocaleString() : 'Open'}</td>
                        <td>{s.duration || '-'}</td>
                        <td>${Number(s.opening_cash || 0).toFixed(2)}</td>
                        <td>${Number(s.expected_cash || 0).toFixed(2)}</td>
                        <td>{s.actual_cash != null ? `$${Number(s.actual_cash).toFixed(2)}` : '-'}</td>
                        <td>{formatVariance(s.variance)}</td>
                        <td>${Number(s.total_sales || 0).toFixed(2)}</td>
                      </tr>
                      {expandedShift === s.id && (
                        <tr>
                          <td colSpan={9} style={{ background: 'var(--gray-50)', padding: '1rem' }}>
                            <strong style={{ fontSize: '0.8rem' }}>Cash Movements:</strong>
                            {expandedMovements.length > 0 ? (
                              <table className="table" style={{ marginTop: '0.5rem' }}>
                                <thead><tr><th>Type</th><th>Amount</th><th>Reason</th><th>Time</th></tr></thead>
                                <tbody>
                                  {expandedMovements.map((m, i) => (
                                    <tr key={i}>
                                      <td>{m.type}</td><td>${Number(m.amount || 0).toFixed(2)}</td><td>{m.reason || '-'}</td>
                                      <td>{m.created_at ? new Date(m.created_at).toLocaleTimeString() : '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>No cash movements</p>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {shifts.length === 0 && !loading && (
                    <tr><td colSpan={9} className="text-center text-muted" style={{ padding: '2rem' }}>No shifts found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="visible-mobile">
            {shifts.map(s => (
              <div className="card" key={s.id} style={{ marginBottom: '0.75rem' }}>
                <div className="card-body">
                  <div className="shifts-mobile-card-top" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <span className="font-bold">{s.staff_name || s.user_name || 'Shift'}</span>
                    <span>{s.closed_at ? 'Closed' : 'Open'}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {s.opened_at ? new Date(s.opened_at).toLocaleString() : ''} | Sales: ${Number(s.total_sales || 0).toFixed(2)}
                  </div>
                  <div style={{ marginTop: '0.25rem' }}>Variance: {formatVariance(s.variance)}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tab 3: Daily Closure */}
      {activeTab === 'daily-closure' && (
        <>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-body">
              <div className="shifts-closure-controls" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Date</label>
                  <input type="date" className="form-input" value={closureDate} onChange={(e) => setClosureDate(e.target.value)} />
                </div>
                <button className="btn btn-primary" onClick={loadDailyClosure} disabled={loading}>Run Daily Closure</button>
              </div>
            </div>
          </div>

          {closureData && (
            <>
              <div className="kpi-grid" style={{ marginBottom: '1.5rem' }}>
                <div className="kpi-card success">
                  <div className="kpi-label">Total Sales</div>
                  <div className="kpi-value">${Number(closureData.total_sales || 0).toFixed(2)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Net Sales</div>
                  <div className="kpi-value">${Number(closureData.net_sales || 0).toFixed(2)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Cash Variance</div>
                  <div className="kpi-value">{formatVariance(closureData.total_variance)}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Shifts Closed</div>
                  <div className="kpi-value">{closureData.shifts_closed || 0}</div>
                </div>
              </div>

              {closureData.shifts && closureData.shifts.length > 0 && (
                <div className="card">
                  <div className="card-header"><h3 className="card-title">Per-Shift Breakdown</h3></div>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr><th>Staff</th><th>Duration</th><th>Sales</th><th>Opening</th><th>Expected</th><th>Actual</th><th>Variance</th></tr>
                      </thead>
                      <tbody>
                        {closureData.shifts.map((s, i) => (
                          <tr key={i}>
                            <td>{s.staff_name || '-'}</td>
                            <td>{s.duration || '-'}</td>
                            <td>${Number(s.total_sales || 0).toFixed(2)}</td>
                            <td>${Number(s.opening_cash || 0).toFixed(2)}</td>
                            <td>${Number(s.expected_cash || 0).toFixed(2)}</td>
                            <td>{s.actual_cash != null ? `$${Number(s.actual_cash).toFixed(2)}` : '-'}</td>
                            <td>{formatVariance(s.variance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {loading && <div className="loading"><div className="spinner"></div><span>Loading...</span></div>}
    </div>
  );
};

export default Shifts;
